import { FLEET_WRITE_TOOL_NAMES } from "@rakazo/contracts";

/**
 * jhl-Bots: a bot rewrites its own instructions when it learns something lasting, and the lead bot
 * (one the owner created, like Seneca) those of any bot in its space, without waiting for the
 * owner's card. A bot another bot created rewrites without a card only the bots it created itself,
 * as K1 did (18.10.2026). Every edit lands in the instruction history, where the owner sees it and
 * can undo it.
 */
const INSTRUCTION_EDIT_TOOLS = new Set(["propose_instructions_update", "set_child_instructions"]);

/**
 * Confirmations of a bot's own writes to bots: like list_fleet in the original, their results bring
 * nothing from outside into the run. Otherwise the first new bot or edit would put every later
 * instruction edit of the same run behind a card.
 */
const BOT_WRITE_TOOLS = new Set<string>([
  "propose_instructions_update",
  "spawn_bot",
  "update_bot",
  ...FLEET_WRITE_TOOL_NAMES,
]);

/** Whether the tool edits instructions and may skip the owner's card in an untainted run. */
export function instructionEditSkipsCard(toolName: string): boolean {
  return INSTRUCTION_EDIT_TOOLS.has(toolName);
}

/**
 * Whether this call runs without a card. A block rule still refuses it, and once outside content
 * entered the run the original gate decides again (a card, or a refusal in Never ask), since that
 * content could have asked for the edit. The callbacks are only asked for instruction edits.
 */
export async function instructionEditRunsWithoutCard(input: {
  toolName: string;
  blocked: boolean;
  /** The caller was created by another bot, not by the owner. */
  callerCreatedByBot: boolean;
  /** Whether the bot whose instructions change was created by the caller. */
  targetCreatedByCaller: () => Promise<boolean>;
  tainted: () => Promise<boolean>;
}): Promise<boolean> {
  if (!instructionEditSkipsCard(input.toolName) || input.blocked) return false;
  if (
    input.toolName === "set_child_instructions" &&
    input.callerCreatedByBot &&
    !(await input.targetCreatedByCaller())
  ) {
    return false;
  }
  return !(await input.tainted());
}

/** Whether the tool's result only confirms the bot's own write to a bot and so leaves the run untainted. */
export function botWriteResultIsOwnState(toolName: string): boolean {
  return BOT_WRITE_TOOLS.has(toolName);
}
